import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { computeChoghadiya, findActiveSlot, diffHMS } from "@/lib/choghadiya";

/**
 * HeroClock — large DIGITAL clock as the hero centerpiece.
 *
 *  - Massive HH : MM : SS digits in brass-foil typography (Space Grotesk).
 *  - Below the digits: weekday + date (in the location's timezone).
 *  - Below that: a brass chip showing the active Choghadiya + countdown
 *    until it ends — tap to open the SlotModal.
 *
 *  No analog faces, no roman numerals — explicit digital readout.
 *  The "top-3 auspicious today" cards live in the dedicated TopAuspicious
 *  section further down the page (do NOT duplicate them here).
 */
const STATE_HEX = {
  amrit_best: "#6E9794",
  shubh_good: "#D89BAA",
  labh_gain:  "#D8B97A",
};

// Get H / M / S / AM-PM in the LOCATION's timezone
function tzClockParts(date, timezone) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone, hour12: true,
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const parts = fmt.formatToParts(date);
  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  return {
    h: get("hour").padStart(2, "0"),
    m: get("minute").padStart(2, "0"),
    s: get("second").padStart(2, "0"),
    ampm: (get("dayPeriod") || "").toUpperCase(),
  };
}

export default function HeroClock({ location, now, onSlotClick }) {
  const cho = useMemo(
    () => computeChoghadiya(location.lat, location.lng, now, location.timezone),
    [location.lat, location.lng, location.timezone, now]
  );
  const active = findActiveSlot(cho, now);
  const tz = location.timezone;

  const { h, m, s, ampm } = tzClockParts(now, tz);
  const diff = active ? diffHMS(active.end, now) : null;

  const weekdayLong = now.toLocaleDateString("en-US", {
    weekday: "long", timeZone: tz,
  });
  const dateLong = now.toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric", timeZone: tz,
  });

  return (
    <div className="relative w-full">
      {/* DIGITAL CLOCK */}
      <div className="relative max-w-[1100px] mx-auto px-4 sm:px-6 md:px-10">
        {/* eyebrow */}
        <div className="flex items-center justify-center gap-3 mb-2 sm:mb-3">
          <span className="h-px w-8 sm:w-14" style={{ background: "var(--accent-soft)", opacity: 0.45 }} />
          <span
            className="font-mono uppercase tracking-[0.32em] text-[9px] sm:text-[10px]"
            style={{ color: "var(--accent-soft)" }}
          >
            local · {tz?.split("/")?.pop()?.replace(/_/g, " ") || "now"}
          </span>
          <span className="h-px w-8 sm:w-14" style={{ background: "var(--accent-soft)", opacity: 0.45 }} />
        </div>

        {/* The actual digital numerals */}
        <div
          data-testid="hero-digital-clock"
          className="flex items-end justify-center gap-1 sm:gap-2 md:gap-4 leading-none tabular"
        >
          <DigitGroup value={h} />
          <Colon />
          <DigitGroup value={m} />
          <Colon faint />
          <DigitGroup value={s} faint />
          {ampm && (
            <span
              className="self-start font-mono mt-1 sm:mt-2 ml-1 sm:ml-2"
              style={{
                color: "var(--ink-faint)",
                letterSpacing: "0.18em",
                fontSize: "clamp(0.65rem, 1.2vw, 0.95rem)",
                fontWeight: 600,
              }}
            >
              {ampm}
            </span>
          )}
        </div>

        {/* Weekday & date */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-4"
        >
          <span
            className="font-display italic"
            style={{
              color: "var(--ink)",
              fontSize: "clamp(0.95rem, 1.6vw, 1.35rem)",
              letterSpacing: "0.01em",
            }}
          >
            {weekdayLong}
          </span>
          <span className="hidden sm:inline" style={{ color: "var(--accent-soft)", opacity: 0.55 }}>·</span>
          <span
            className="font-mono uppercase"
            style={{
              color: "var(--ink-muted)",
              letterSpacing: "0.20em",
              fontSize: "clamp(0.62rem, 1vw, 0.8rem)",
            }}
          >
            {dateLong}
          </span>
        </motion.div>

        {/* Active-Choghadiya chip */}
        {active && (
          <motion.button
            onClick={() => onSlotClick?.(active)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            data-testid="hero-clock-active"
            className="mt-4 sm:mt-5 mx-auto flex items-center justify-center gap-2.5 sm:gap-4 max-w-fit glass-deep rounded-full px-4 sm:px-6 py-2 sm:py-2.5 hover:translate-y-[-2px] transition-transform duration-500"
            style={{
              borderColor: `${STATE_HEX[active.stateKey] || "#D8B97A"}66`,
            }}
          >
            <span
              className="w-2 h-2 rounded-full pulse-ring shrink-0"
              style={{ background: STATE_HEX[active.stateKey] || "#D8B97A" }}
            />
            <span
              className="font-mono uppercase tracking-[0.22em] text-[9px] sm:text-[10px]"
              style={{ color: STATE_HEX[active.stateKey] || "#D8B97A" }}
            >
              now · {active.subtext.toLowerCase()}
            </span>
            <span
              className="font-display"
              style={{
                color: "var(--ink)",
                fontSize: "clamp(0.9rem, 1.4vw, 1.1rem)",
                fontWeight: 500,
              }}
            >
              {active.name}
            </span>
            {diff && (
              <span className="hidden sm:inline numeric text-xs sm:text-sm" style={{ color: "var(--accent-soft)", letterSpacing: "0.06em" }}>
                {String(diff.h).padStart(2, "0")}:{String(diff.m).padStart(2, "0")}:{String(diff.s).padStart(2, "0")}
              </span>
            )}
            <ArrowUpRight size={13} style={{ color: "var(--accent-soft)" }} />
          </motion.button>
        )}
      </div>
    </div>
  );
}

/* ----------------------- Subcomponents ----------------------- */

function DigitGroup({ value, faint = false }) {
  if (faint) {
    return (
      <span
        className="numeric inline-flex"
        style={{
          fontFamily: "'Space Grotesk', 'JetBrains Mono', monospace",
          fontWeight: 500,
          fontSize: "clamp(2.4rem, 9vw, 6.5rem)",
          lineHeight: 0.9,
          letterSpacing: "-0.02em",
          color: "var(--ink-faint)",
          opacity: 0.55,
        }}
      >
        {value}
      </span>
    );
  }
  return (
    <span
      className="numeric inline-flex"
      style={{
        fontFamily: "'Space Grotesk', 'JetBrains Mono', monospace",
        fontWeight: 500,
        fontSize: "clamp(2.4rem, 9vw, 6.5rem)",
        lineHeight: 0.9,
        letterSpacing: "-0.02em",
        background: "linear-gradient(180deg, #F2E2B0 0%, #D8B97A 35%, #B89B5E 70%, #8C7642 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        color: "transparent",
        textShadow: "0 1px 0 rgba(140, 118, 66, 0.25)",
      }}
    >
      {value}
    </span>
  );
}

function Colon({ faint = false }) {
  return (
    <motion.span
      animate={{ opacity: faint ? [0.25, 0.55, 0.25] : [0.5, 1, 0.5] }}
      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      className="inline-flex items-center justify-center"
      style={{
        fontFamily: "'Space Grotesk', monospace",
        fontWeight: 400,
        fontSize: "clamp(1.8rem, 7vw, 5rem)",
        lineHeight: 0.9,
        color: faint ? "var(--ink-faint)" : "var(--accent-soft)",
        transform: "translateY(-6%)",
      }}
    >
      :
    </motion.span>
  );
}
